// src/molecules/Node/Node.tsx

import React, {
  useState,
  useRef,
  FC,
  useMemo,
  useCallback,
} from 'react';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import { useTranslation } from 'localization';
import './Node.scss'; // Optional for custom styles
import StatusBadge from '../../../atoms/status-badge/StatusBadgeAtom';
import { OverlayPanel } from 'primereact/overlaypanel';
import 'primeflex/primeflex.css';
import { Sphere } from 'types/global';
import AtlasPerspectiveSelector from '../AtlasPerspectiveSelector/AtlasPerspectiveSelector';
import StatusPopoverMolecule from '../StatusPopover/StatusPopoverMolecule';
import NodeCounts from '../NodeCounts/NodeCounts';
import NodeLabel from '../NodeLabel/NodeLabel';
import NodePerspectivePill from '../NodePerspectivePill/NodePerspectivePill';
import { AtlasViewContext } from '../../../organisms/AtlasComponent/AtlasViewContext';
import AtlasSpherePropertyMolecule from '../AtlasSpherePropertyMolecule/AtlasSpherePropertyMolecule';
import AtlasArcStageSelectorMolecule from '../AtlasArcStageSelector/AtlasArcStageSelectorMolecule';
import { PermissionList } from '../../../../../types/sharedTypes';
import MetaspaceSelector from '../../MetaspaceMenu/MetaspaceMenu';
import { getMoleculeVisibility } from '../../../../utils/responsiveColumns';
export interface OptionType {
  name: string;
  code: string;
  avatar?: string;
  image?: string;
}
import { useNavigate } from 'react-router-dom';
import RichTextAtom from '../../../atoms/rich-text/RichTextAtom';
import { resolveLabel } from '../../../../utils/resolveLabel';
import ChipAtom from '../../../atoms/Chip/ChipAtom';
import FinishSetupEditorMolecule from '../../FinishSetupEditor/FinishSetupEditorMolecule';

interface NodeProps {
  sphere: Sphere;
  level: number;
  isParentNode: boolean;
  breadcrumbPath: string;
  onBreadcrumbClick: (part: string, event: React.MouseEvent) => void;
  isSelected: boolean;
  onActiveSectionChange: (
    section: string,
    selectedNode: Sphere
  ) => Promise<void>;
  onEdgeCountClick: (sphereId: string, level: number) => void;
  onNodeClick: () => void;
  selectedEdgeCountNode: string | null;
  onTraversalDirectionClick?: (
    sphereId: string,
    direction: 'outwards' | 'inwards' | 'both',
    level: number
  ) => void;
  traversalDirection: 'outwards' | 'inwards' | 'both';
  isVisibleGlobal: boolean;
  onGlobalToggle: () => void;
  widthConfig?: {
    labelWidth?: string;
    roleWidth?: string;
    orgDetailsWidth?: string;
    perspectivesWidth?: string;
    countsWidth?: string;
    perspectivesResponsibleWidth?: string;
  };
  // In Node.tsx props interface
  headerWidths?: {
    label: any;
    width: number;
    className: string;
    perspectiveSystemName: string;
    nodeMoleculeIndex?: number;
  }[];

  fetchPermissionList: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
  /**
   * Callback for when a property of the sphere changes.
   * The function takes the node ID, property name, and new value as arguments.
   */
  onPropertyChange?: (
    nodeId: string,
    propertyName: string,
    newValue: string | null
  ) => Promise<void>;
  onPerspectiveAssignmentChange: (
    sphereId: string,
    perspectiveSystemName: string,
    assignee: string | null
  ) => Promise<void>;
  onPerspectiveAssignmentsChange?: (
    sphereId: string,
    perspectiveSystemName: string,
    assignees: string[] | null,
    sphereObject?: any
  ) => Promise<void>;
  parentNode?: Sphere;
  notification?: { type: 'success' | 'error'; message: string; timer?: number };
  onNotificationDismiss?: (sphereId: string) => void;
  onShowAddSphere: (parentSphere?: Sphere, grandparentSphere?: Sphere) => void;
  onShowEditSphere: (
    sphere: Sphere,
    parentSphere: Sphere,
    viewMode: boolean,
    initialTab?: string
  ) => void;
  onDeactivateNode: (nodeId: string) => void;
  /** Callback when user wants to configure status factors - opens SphereEditor to Status Factors tab */
  onConfigureStatusFactors?: (sphereId: string) => void;
  isExpandedByEdgeClick?: boolean;
  /** Whether this node's own subtree is expanded — carries `aria-expanded` on the row's interactive label (S6811: the row's `<li>` has an implicit `listitem` role, which does not support the attribute). */
  isTreeExpanded?: boolean;
  onChatBubbleClick?: (sphereId: string) => void;
  /**
   * Set only when Atlas is rendered inside a metaspace with the toolbar active.
   * When present, clicking the row's status icon opens the toolbar's status tool
   * scoped to this sphere instead of toggling the inline StatusPopover. Undefined
   * for standalone Atlas, where the popover remains the status affordance.
   */
  onStatusToolClick?: (sphereId: string) => void;
  /** Callback when sphere status changes (e.g., after check-in) to trigger ancestor polling */
  onStatusChange?: (sphereId: string, ancestorIds: string[]) => void;
  /** Callback when a draft sphere is finalized — triggers a tree refresh. */
  onSphereFinalized?: (sphere: Sphere) => void;
  /** Show "Deploy approach" in the row menu. */
  showDeployApproach?: boolean;
  /** Called when user selects "Deploy approach" from the row menu. */
  onShowDeployApproach?: (sphere: Sphere) => void;
  /**
   * `nodeMolecules` index of the column selected in the Atlas view editor's live
   * preview. When set, the cell rendered for that molecule carries the selection
   * tint (`node-cell--selected`), matching `atlas-header`'s `selectedColumnIndex`
   * so one click confirms selection at both the header and the cell. Undefined on
   * the real board, where no column is ever "selected".
   */
  selectedColumnIndex?: number | null;
  /**
   * Tag each column cell with `data-atlas-column-index` (and wrap it so a selected
   * cell has a paintable box). Set only by the view editor's live preview, which
   * asserts its indices against `atlas-header`'s. The real board leaves this off so
   * its DOM keeps exactly the shape its CSS is written against — a wrapper element
   * makes every cell the only child of its own parent, which silently changes what
   * positional selectors like `:last-child` match.
   */
  tagColumnIndices?: boolean;
}

const Node: FC<NodeProps> = React.memo(
  ({
    sphere,
    level,
    isParentNode,
    breadcrumbPath,
    onBreadcrumbClick,
    isSelected,
    onActiveSectionChange,
    onEdgeCountClick,
    onNodeClick,
    selectedEdgeCountNode,
    onTraversalDirectionClick,
    traversalDirection,
    isVisibleGlobal = true,
    onGlobalToggle,
    widthConfig = {},
    fetchPermissionList,
    onPropertyChange,
    onPerspectiveAssignmentChange,
    onPerspectiveAssignmentsChange,
    parentNode,
    notification,
    onNotificationDismiss,
    onShowAddSphere,
    onShowEditSphere,
    onDeactivateNode,
    onConfigureStatusFactors,
    isExpandedByEdgeClick = false,
    isTreeExpanded,
    headerWidths = [],
    onChatBubbleClick,
    onStatusToolClick,
    onStatusChange,
    onSphereFinalized,
    showDeployApproach = false,
    onShowDeployApproach,
    selectedColumnIndex = null,
    tagColumnIndices = false,
  }) => {
    const { t, i18n } = useTranslation();
    const charLimit = 100; // Character limit for truncation

    const { schema: sphereSchema } = useSchemaType(sphere.properties.type ?? null);

    const context = React.useContext(AtlasViewContext);
    const { viewConfig, responsiveBreakpoint } = context ?? {};
    const [expandedSphereIds, setExpandedSphereIds] = useState<string[]>([]);
    // Initialize all hooks before any conditional logic
    const [activeSection, setActiveSection] = useState<string>('node-role');
    const organizationId = sphere.properties?.sphereId;
    const nodeRef = useRef<HTMLDivElement>(null);
    const statusPopoverRef = useRef<OverlayPanel>(null);

    const handleEdgeClick = (sphereId: string) => {
      setExpandedSphereIds((prev) =>
        prev.includes(sphereId)
          ? prev.filter((id) => id !== sphereId)
          : [...prev, sphereId]
      );
    };

    const handleStatusClick = (event: React.MouseEvent) => {
      // Inside a metaspace toolbar, the status icon opens the toolbar's status
      // tool scoped to this row's sphere; otherwise it toggles the inline popover.
      if (onStatusToolClick) {
        onStatusToolClick(sphere.id);
        return;
      }
      statusPopoverRef.current?.toggle(event);
    };
    
  
    // Ensure useMemo is called unconditionally
    const nodeStyle = useMemo(
      () => ({
        fontWeight: isSelected ? 'bold' : 'normal',
      }),
      [isSelected]
    );

    // Draft sphere state
    const isDraft = !!sphere.properties.isDraft;
    const [isFinishSetupOpen, setIsFinishSetupOpen] = useState(false);

    const missingRequiredFields = useMemo(() => {
      if (!isDraft || !sphereSchema?.properties) return [];
      return (sphereSchema.properties as any[])
        .filter(
          (p: any) =>
            p.required === true &&
            p.system_name !== 'center_name' &&
            p.system_name !== 'center_type' &&
            !sphere.properties[p.system_name],
        )
        .map((p: any) => p.name || p.system_name);
    }, [isDraft, sphereSchema, sphere.properties]);

    // This was true by default and sonar is complaning that setIsActive is not used. Adding this until we determine if needed.
    const isActive = true;

    const navigate = useNavigate();

    // 2. Define a callback for when a metaspace is selected
    const handleMetaspaceSelected = useCallback(
      (selectedMetaspaceId: string) => {
        // Construct the route. For example:
        // /sphere/:sphereId/metaspace/:metaspaceId
        navigate(`/sphere/${sphere.id}/metaspace/${selectedMetaspaceId}`);
      },
      [navigate, sphere.id]
    );

    const activeStyles = useMemo(
      () => (isSelected ? { border: '2px solid #51A0DC' } : {}),
      [isSelected]
    );

    const connectingPerspectives = sphere.connectingPerspectives || [];
    const connectingDirection =
      sphere.connectingPerspectives?.[0]?.direction ?? 'outwards';
    const connectingArrows = `connecting-${connectingDirection}`;

    // Helper function to get perspective display name from system_name
    const getPerspectiveDisplayName = (systemName: string) => {
      if (sphereSchema?.perspectives) {
        const perspective = sphereSchema.perspectives.find((p: any) => p.system_name === systemName);
        if (perspective?.name) {
          return perspective.name;
        }
      }

      // Fallback: convert system_name to readable format
      return systemName
        .replace('center_', '')
        .replaceAll('_', ' ')
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const perspectivePill = useMemo(
      () =>
        connectingPerspectives?.length > 0
          ? connectingPerspectives
              .map((perspective) =>
                getPerspectiveDisplayName(
                  perspective.system_name || perspective.name || '',
                )
              )
              .filter((name) => name)
              .join(', ') || ' '
          : null,
      [connectingPerspectives, sphereSchema]
    );

    const handleSectionClick = useCallback((sectionName: string) => {
      setActiveSection(sectionName); // Set the clicked section as active
    }, []);

    // **Handle Active Section Change by Passing Both Section and Sphere**
    const handleActiveSectionChangeInternal = useCallback(
      async (section: string) => {
        await onActiveSectionChange(section, sphere); // Pass the current sphere
      },
      [onActiveSectionChange, sphere]
    );

    const handlePropertyChanged = useCallback(
      async (
        sphereId: string,
        propertyName: string,
        newValue: string | null
      ) => {
        if (onPropertyChange) {
          await onPropertyChange(sphereId, propertyName, newValue);
        } else {
          console.warn('onPropertyChange function not provided.');
        }
      },
      [sphere.id, onPropertyChange]
    );
    
    const columnsStyle: Record<string, string> = useMemo(() => {
      // Safe helper: `?.width + 'px'` coerces undefined to 'undefinedpx' (truthy),
      // so the `|| fallback` path never fires on a miss. Return '' on a miss — an
      // empty string removes the inline width so the CSS rule (`width: 59rem`) acts
      // as the pre-measurement fallback, keeping content visible until widths arrive.
      const w = (fn: (h: NonNullable<typeof headerWidths>[0]) => boolean): string => {
        const found = headerWidths?.find(fn);
        return found?.width ? `${found.width}px` : '';
      };

      const arcHeader = headerWidths?.find((h) => h.className === 'col-1 node-arc-stage');

      // node-content in the data row wraps ALL topContentMolecules (NodeLabel + ApproachBadges).
      // ApproachBadges has its own header cell (col-1 node-approach-badges) that appears
      // BEFORE the col-6 NodeLabel cell in the DOM. Both must be summed so node-content
      // spans the full width that these two header cells occupy together.
      const approachBadgesHeaderWidth =
        headerWidths?.find((h) => h.className === 'col-1 node-approach-badges')?.width ?? 0;
      const nodeLabelHeaderWidth = headerWidths?.find((h) => h.className === 'col-6')?.width ?? 0;
      const nodeContentHeaderTotal = approachBadgesHeaderWidth + nodeLabelHeaderWidth;

      return {
        nodeLabel: nodeContentHeaderTotal > 0 ? `${nodeContentHeaderTotal}px` : '',
        status: w((h) => h.className === 'status-header'),
        description: w((h) => h.label === 'Description'),
        flow: arcHeader?.width ? `${arcHeader.width}px` : '',
      };
    }, [headerWidths]);
    
    
    // **Handle Assigned to Perspective Change by Forwarding the Callback**
    const handleAssignedToPerspectiveChange = useCallback(
      async (
        assignee: string | null,
        sphereId: string,
        perspectiveSystemName: string
      ) => {
        if (onPerspectiveAssignmentChange) {
          await onPerspectiveAssignmentChange(
            sphereId,
            perspectiveSystemName,
            assignee
          );
        } else {
          console.warn('onPerspectiveAssignmentChange function not provided.');
        }
      },
      [onPerspectiveAssignmentChange]
    );

    const handleAssignedToPerspectivesChange = useCallback(
      async (
        assignees: string[] | null,
        sphereId: string,
        perspectiveSystemName: string
      ) => {
        // Don't save immediately in Atlas - only update local state
        // Actual save happens when user clicks "Done" via handleDone callback
      },
      []
    );

    const handleDone = useCallback(
      async (
        sphereId: string,
        perspectiveSystemName: string,
        assignees: string[]
      ) => {
        if (onPerspectiveAssignmentsChange) {
          await onPerspectiveAssignmentsChange(
            sphereId,
            perspectiveSystemName,
            assignees,
            sphere // Pass the sphere object so AtlasTree doesn't need to look it up
          );
        } else {
          console.warn('onPerspectiveAssignmentsChange function not provided - cannot save.');
        }
      },
      [onPerspectiveAssignmentsChange, sphere]
    );

    const handleNodeResponsiblePerspectivesClick = useCallback(
      (section: string) => {
        handleSectionClick(section);

        if (activeSection === section) {
          setActiveSection('');
          handleActiveSectionChangeInternal(''); // Pass empty string
        } else {
          setActiveSection(section);
          handleActiveSectionChangeInternal(section);
        }
      },
      [activeSection, handleSectionClick, handleActiveSectionChangeInternal]
    );
    const nodeMolecules = viewConfig?.nodeMolecules ?? [];
    // Conditional rendering based on organizationId
    if (!organizationId) {
      console.error('Organization ID not found in the store');
      return null;
    }

    const topContentMolecules: JSX.Element[] = [];
    const bottomContentMolecules: JSX.Element[] = [];

    // Wraps a molecule's rendered cell with the editor live-preview's selection tint.
    // Unselected cells wrap in `display: contents`, which keeps the wrapper out of
    // layout entirely — it changes nothing when `selectedColumnIndex` is unset (every
    // real board render) or doesn't match this column. A `display: contents` element
    // generates no box of its own, so it can never paint a background/outline — the
    // selected cell instead wraps in `display: inline-flex`, which DOES generate a
    // paintable box. `flex: 1 1 auto; min-width: 0` keeps the wrapper filling the same
    // space its child previously claimed directly (e.g. `NodeLabel`, which has no
    // intrinsic width of its own and relies on `width: 100%` against its flex parent —
    // without this the wrapper shrink-wraps to content and collapses that column).
    // Also tags every column cell with `data-atlas-column-index`, mirroring the same
    // attribute `atlas-header` puts on its `<li>` — the live preview asserts the two
    // sets of indices match, which is how "header/data-row cell counts align" is
    // actually verified rather than merely hoped for (spec Phase 4).
    const withColumnSelection = (
      el: JSX.Element | null,
      moleculeIndex: number
    ): JSX.Element | null => {
      if (!el || !tagColumnIndices) return el;
      const isSelected = selectedColumnIndex === moleculeIndex;
      return (
        <span
          key={`atlas-col-select-${moleculeIndex}`}
          data-atlas-column-index={moleculeIndex}
          className={isSelected ? 'node-cell--selected' : undefined}
          style={
            isSelected
              ? { display: 'inline-flex', alignItems: 'stretch', flex: '1 1 auto', minWidth: 0 }
              : { display: 'contents' }
          }
        >
          {el}
        </span>
      );
    };

    nodeMolecules.forEach((molecule: any, index: number) => {
      const { moleculeProps = {} } = molecule;

      // Use new responsive visibility system instead of simple toolbar expansion check
      const visibilityConfig = getMoleculeVisibility(
        molecule.type,
        moleculeProps?.important === true,
        responsiveBreakpoint ?? 'full',
        moleculeProps
      );

      // Skip rendering if molecule should not be visible at this breakpoint
      if (!visibilityConfig.isVisible) {
        return;
      }

      let element: JSX.Element | null = null;

      switch (molecule.type) {
        // NodeCounts / NodePerspectivePill are fixed elements, not authored columns —
        // they render from view-level flags below, outside this loop, regardless of
        // whether `nodeMolecules` still carries an entry for them (spec Phase 2,
        // EAINT-857). Falling through to `default` here means a still-present entry
        // (pre-migration data) is simply ignored rather than double-rendered.
        case 'NodeLabel':
          element = (
            <React.Fragment key={`node-label-${sphere.id}-${index}`}>
              <NodeLabel
                isExpandedByEdgeClick={isExpandedByEdgeClick}
                sphere={sphere}
                parentNode={parentNode}
                level={level}
                isParentNode={isParentNode}
                breadcrumbPath={breadcrumbPath}
                onBreadcrumbClick={onBreadcrumbClick}
                onNodeClick={onNodeClick}
                isTreeExpanded={isTreeExpanded}
                charLimit={charLimit}
                fetchPermissionList={fetchPermissionList}
                notification={notification}
                onNotificationDismiss={onNotificationDismiss}
                onShowAddSphere={onShowAddSphere}
                onShowEditSphere={onShowEditSphere}
                onDeactivateNode={onDeactivateNode}
                expandedSphereIds={expandedSphereIds}
                onChatBubbleClick={onChatBubbleClick}
                showDeployApproach={showDeployApproach}
                onShowDeployApproach={onShowDeployApproach}
              />
              <div className="relative-wrapper">
                <span className="meta-selector mt-2 ml-1">
                  <MetaspaceSelector
                    sphere={sphere}
                    metaspaceSelected={handleMetaspaceSelected}
                  />
                </span>
              </div>
            </React.Fragment>
          );
          topContentMolecules.push(withColumnSelection(element, index)!);
          break;

        case 'NodeDetailsButton':
          element = (
            <div
              key={`node-details-${sphere.id}-${index}`}
              className="node-org-details pr-0 pl-0"
              style={{ width: columnsStyle.description, overflow: 'hidden' }}
              title={(sphere.properties.description ?? '').replaceAll(/<[^>]*>/g, '').trim() || t('common.no_description', 'No description provided')}
            >
              <RichTextAtom
                content={
                  sphere.properties.description ?? t('common.no_description', 'No description provided')
                }
                className="node-parent node-parent--truncated"
              />
            </div>
          );
          bottomContentMolecules.push(withColumnSelection(element, index)!);
          break;

        case 'ArcStageSelector':
        case 'FlowStageSelector': {
          // Apply responsive width constraints
          let width = columnsStyle.flow || '220px';
          if (visibilityConfig.maxWidth) width = visibilityConfig.maxWidth;
          if (visibilityConfig.minWidth) width = visibilityConfig.minWidth;

          const arcClassName = `node-parent ${visibilityConfig.className || ''}`.trim();

          // Count non-primary arcs with pending actions (active stage = needs attention)
          const nonPrimaryPendingCount = (sphere.arcs ?? []).filter(
            a => !a.isPrimary && a.currentStageId
          ).length;

          element = (
            <div
              key={`arc-stage-${sphere.id}-${index}`}
              className={`atlas-arc-column ${arcClassName}`}
              style={{ width }}
            >
              <AtlasArcStageSelectorMolecule
                sphere={sphere}
                fetchPermissionList={fetchPermissionList}
                onStageUpdated={(sphereId, propName, updatedArc) => {
                  const arcJson = JSON.stringify(updatedArc);
                  return handlePropertyChanged(sphereId, propName, arcJson);
                }}
                className=""
                style={{ flex: 1 }}
              />
              {nonPrimaryPendingCount > 0 && (
                <span
                  className="arc-pending-badge"
                  title={t('arc.atlas.pending_actions', { count: nonPrimaryPendingCount })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowEditSphere(sphere, parentNode ?? sphere, false, 'arcs-tab');
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      onShowEditSphere(sphere, parentNode ?? sphere, false, 'arcs-tab');
                    }
                  }}
                >
                  ⚡{nonPrimaryPendingCount}
                </span>
              )}
            </div>
          );
          bottomContentMolecules.push(withColumnSelection(element, index)!);
          break;
        }

        case 'PerspectiveSelector': {
          const { perspectiveSystemName, assignable, display, maxAvatars, placeholder } = moleculeProps;

          const matchingHeader = headerWidths.find(
            (h) => h.perspectiveSystemName === perspectiveSystemName
          );

          // Apply responsive width constraints
          let width = matchingHeader?.width ? `${matchingHeader.width}px` : '150px';
          if (visibilityConfig.maxWidth) width = visibilityConfig.maxWidth;
          if (visibilityConfig.minWidth) width = visibilityConfig.minWidth;

          const className = `node-parent ${visibilityConfig.className || ''}`.trim();

          // Get cardinality from schema instead of sphere.perspectives.
          // sphereSchema comes from useSchemaType() at the top of the component.
          const perspectiveDefinition = sphereSchema?.perspectives?.find(
            (p: any) => p.system_name === perspectiveSystemName
          );
          const cardinality = perspectiveDefinition?.cardinality || 'single';

          element = (
            <AtlasPerspectiveSelector
              key={`atlas-perspective-${perspectiveSystemName}-${sphere.id}`}
              perspectiveSystemName={perspectiveSystemName}
              sphere={sphere}
              parentNode={parentNode}
              organizationId={organizationId}
              isSelected={isSelected}
              activeSection={activeSection}
              activeStyles={activeStyles}
              className={className}
              onAssigneeSelected={handleAssignedToPerspectiveChange}
              onAssigneesSelected={handleAssignedToPerspectivesChange}
              onDone={handleDone}
              cardinality={cardinality}
              style={{ width }}
              assignable={assignable !== false}
              display={display}
              maxAvatars={maxAvatars}
              placeholder={placeholder}
            />
          );
          if (element) bottomContentMolecules.push(withColumnSelection(element, index)!);
          break;
        }

        case 'Aspect': {
          const {
            aspect,
            isFriendly,
            choiceStyle,
            maxValues,
            overflow,
            showAvatar,
            numberFormat,
            characterLimit,
            fallbackLabelKey,
          } = moleculeProps;
          if (!aspect) break;

          // Apply responsive width constraints — keyed by the column's own
          // `nodeMoleculeIndex` (mirrors the `PerspectiveSelector` case above), not
          // by the aspect's label. The label map this replaced only special-cased a
          // handful of English aspect labels and silently fell back to '150px' for
          // every other Aspect column, regardless of its real measured header width.
          const matchingAspectHeader = headerWidths.find(
            (h) => h.nodeMoleculeIndex === index
          );
          let width = matchingAspectHeader?.width ? `${matchingAspectHeader.width}px` : '150px';
          if (visibilityConfig.maxWidth) width = visibilityConfig.maxWidth;
          if (visibilityConfig.minWidth) width = visibilityConfig.minWidth;

          const className = `node-parent ${visibilityConfig.className || ''}`.trim();

          element = (
            <AtlasSpherePropertyMolecule
              key={`sphere-property-${sphere.id}-${aspect}-${index}`}
              sphere={sphere}
              propertyKey={aspect}
              isFriendly={isFriendly}
              choiceStyle={choiceStyle}
              maxValues={maxValues}
              overflow={overflow}
              showAvatar={showAvatar}
              numberFormat={numberFormat}
              characterLimit={characterLimit}
              fallbackLabelKey={fallbackLabelKey}
              fetchPermissionList={fetchPermissionList}
              onDateChanged={(sphereId, propName, newDate) =>
                handlePropertyChanged(sphereId, propName, newDate)
              }
              className={className}
              style={{ width }}
            />
          );
          bottomContentMolecules.push(withColumnSelection(element, index)!);
          break;
        }

        case 'ApproachBadges': {
          const approaches = sphere.deployedApproaches || [];
          if (approaches.length === 0) break;

          const maxVisible = 3;
          const visible = approaches.slice(0, maxVisible);
          const overflow = approaches.length - maxVisible;
          const locale = i18n.language;

          element = (
            <div
              key={`approach-badges-${sphere.id}-${index}`}
              className="atlas-approach-badges"
            >
              {visible.map((approach, i) => {
                const name =
                  resolveLabel(approach.approachName, locale) ||
                  approach.approachSystemName;
                return (
                  <span
                    key={`${approach.approachSystemName}-${i}`}
                    className={`atlas-approach-badge ${
                      approach.isDirect
                        ? 'atlas-approach-badge--direct'
                        : 'atlas-approach-badge--inherited'
                    }`}
                    title={name}
                  >
                    {name}
                  </span>
                );
              })}
              {overflow > 0 && (
                <span
                  className="atlas-approach-badge atlas-approach-badge--overflow"
                  title={t('sphereApproaches.badges.overflow', {
                    count: overflow,
                  })}
                >
                  +{overflow}
                </span>
              )}
            </div>
          );
          topContentMolecules.push(withColumnSelection(element, index)!);
          break;
        }

        default:
          return null;
      }
    });

    // Fixed elements: NodeCounts / NodePerspectivePill (spec Phase 2, EAINT-857).
    // Neither is an authored column any more — both render purely from view-level
    // flags, never from `nodeMolecules`, so a view whose molecule entry was stripped
    // (post-migration) or never had one (flag-only authoring) renders identically to
    // one that still carries the legacy entry.
    // They lead the row, ahead of every authored column. Stored configs have always
    // ordered them `[NodePerspectivePill, NodeCounts, NodeLabel, …]`, and `.node-content`
    // lays out in a flex row — so appending them would push both pills to the right of
    // the sphere name and silently re-order every existing board.
    const fixedElements: JSX.Element[] = [];

    const showPerspectivePill = viewConfig?.showTraversalArrows === true;
    if (showPerspectivePill) {
      fixedElements.push(
        <NodePerspectivePill
          key={`node-perspective-pill-${sphere.id}`}
          perspectivePill={perspectivePill}
          isVisibleGlobal={isVisibleGlobal}
          connectingArrows={connectingArrows}
          onToggleVisibility={onGlobalToggle}
        />
      );
    }

    // Default true, matching NodeCounts' own internal gate — the same semantics the
    // flag has always had via the (now-removed) molecule-driven path.
    const showCounts = viewConfig?.showNearbyNodeCount !== false;
    if (showCounts) {
      // The count is a "complete" (fully rounded) pill unless a NodePerspectivePill
      // actually renders to its left to butt against — which only happens when
      // traversal arrows are on AND this node has a connecting perspective. Keying
      // off showTraversalArrows alone left a square left edge whenever the pill was
      // absent (the common hierarchy view).
      const completePill = !(showPerspectivePill && perspectivePill);
      fixedElements.push(
        <NodeCounts
          key={`node-counts-${sphere.id}`}
          counts={sphere.edgeCount ?? 0}
          sphereId={sphere.id}
          level={level}
          onEdgeCountClick={() => {
            onEdgeCountClick(sphere.id, level);
            handleEdgeClick(sphere.id);
          }}
          traversalDirection={traversalDirection}
          onTraversalDirectionClick={onTraversalDirectionClick}
          width={widthConfig.countsWidth}
          completePill={completePill}
        />
      );
    }

    topContentMolecules.unshift(...fixedElements);

    return (
      <div ref={nodeRef} className={`grid atlas-grid p-dir-col${isDraft ? ' atlas-grid--draft' : ''}`}>
        <div
          ref={nodeRef}
          className="node-content"
          style={{ width: columnsStyle.nodeLabel ? `calc(${columnsStyle.nodeLabel} - 7px)` : '59rem' }}
        >
          {topContentMolecules}
          {isDraft && (
            <ChipAtom
              label={t('draft.badge', { count: missingRequiredFields.length })}
              icon="pi pi-pencil"
              className="draft-badge-chip"
            />
          )}
        </div>
        <div
          className="node-status pt-2"
          style={{ width: columnsStyle.status, cursor: 'pointer' }}
          onClick={handleStatusClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleStatusClick(e as unknown as React.MouseEvent);
            }
          }}
        >
          {sphere.status?.currentValue?.status ? (
            <StatusBadge
              status={sphere.status.currentValue.status}
            />
          ) : (
            <StatusBadge status="unknown" />
          )}
          <StatusPopoverMolecule
            ref={statusPopoverRef}
            sphereId={sphere.id}
            sphereName={sphere.properties.name}
            schemaType={sphere.properties?.type}
            status={sphere.status}
            fetchPermissionList={fetchPermissionList}
            onStatusRefreshed={() => {
              // Trigger ancestor polling after status change (e.g., check-in)
              // Extract ancestor IDs from the ancestors array
              const ancestorIds = sphere.ancestors?.map(a => a.id) || [];
              onStatusChange?.(sphere.id, ancestorIds);
            }}
            onConfigureFactors={onConfigureStatusFactors}
          />
        </div>
        {bottomContentMolecules.map((molecule, index) => {
          if (molecule.type === AtlasPerspectiveSelector) {
            const className =
              index === bottomContentMolecules.length - 1
                ? `${molecule.props.className} col`
                : `${molecule.props.className} xl:col-1`;
            return React.cloneElement(molecule, {
              className,
              key: `perspective-${molecule.props.perspectiveSystemName}-${molecule.props.sphere.id}`
            });
          }
          return React.cloneElement(molecule, {
            key: `molecule-${index}-${molecule.type?.name || 'unknown'}`
          });
        })}
        {isDraft && (
          <div className="draft-finish-setup-trigger">
            <button
              type="button"
              className="draft-finish-setup-trigger__btn"
              onClick={() => setIsFinishSetupOpen((o) => !o)}
            >
              <i className="pi pi-list-check" />
              {t('draft.finishSetup')}
            </button>
          </div>
        )}
        {isDraft && (
          <FinishSetupEditorMolecule
            sphere={sphere}
            requiredFields={missingRequiredFields}
            isExpanded={isFinishSetupOpen}
            onFinalized={(updated) => {
              setIsFinishSetupOpen(false);
              onSphereFinalized?.(updated);
            }}
            onCollapse={() => setIsFinishSetupOpen(false)}
          />
        )}
      </div>
    );
  }
);
export default Node;
